export interface SalesforceResponseError {
  type: string
  detail: string
  title: string
  statusMessage?: string // move the optional statusMessage to the SalesforceResponseError
}

export interface SalesForceError extends Error {
  response: {
    json: () => Promise<SalesforceResponseError>
  }
}

export function isSalesForceError(error: Error): error is SalesForceError {
  return error && 'response' in error
}

async function extractSalesforceErrorResponse(error: SalesForceError): Promise<SalesforceResponseError> {
  return await error.response.json()
}

export function createCustomError(errorResponse: SalesforceResponseError, cause: Error) {
  return new Error(JSON.stringify({ ...errorResponse, message: cause.message }), { cause })
}

export function getErrorWithMessage(cause: unknown) {
  if (typeof cause === 'object' && cause !== null && 'message' in cause && typeof cause.message === 'string') {
    // SAFETY: only `message` is read downstream, and it was checked to be a string above.
    return cause as Error
  }

  try {
    return new Error(JSON.stringify(cause))
  } catch {
    return new Error(String(cause))
  }
}

// Server logs split multi-line output into one entry per line, and logging the Error itself dumps
// its cause chain, including the SDK's fetch Response. Keep it to the messages.
export function errorLogLine(error: Error): string {
  const cause = error.cause instanceof Error ? error.cause.message : ''
  const line = cause && !error.message.includes(cause) ? `${error.message} (cause: ${cause})` : error.message
  return line.replace(/\s*\n\s*/g, ' ')
}

export async function salesforceErrorHandler(cause: unknown) {
  if (!(cause instanceof Error)) {
    return getErrorWithMessage(cause)
  }

  if (!isSalesForceError(cause)) return cause

  const errorResponse = await extractSalesforceErrorResponse(cause)
  return createCustomError(errorResponse, cause)
}
